import React, { memo, useCallback, useEffect, useRef } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { isActivityRequired, isTaskRequired } from "@/shared/utils/firm";

import { AlertTriangle, ArrowDown, ArrowDownUp, ArrowUp } from "lucide-react";
import ResizableColumn from "../ResizableColumn";
import adobeIcon from "../../../../assets/applications/AdobeIcon.svg";
import calendarIcon from "../../../../assets/applications/CalendarIcon.svg";
import chromeIcon from "../../../../assets/applications/ChromeIcon.svg";
import combineIcon from "../../../../assets/applications/CombineIcon.svg";
import combineIconWhite from "../../../../assets/applications/CombineIconWhite.svg";
import duplicateIcon from "../../../../assets/applications/DuplicateIcon.svg";
import duplicateIconWhite from "../../../../assets/applications/DuplicateIconWhite.svg";
import excelIcon from "../../../../assets/applications/ExcelIcon.svg";
import foxitIcon from "../../../../assets/applications/FoxitIcon.svg";
import kofaxIcon from "../../../../assets/applications/KofaxIcon.svg";
import outlookIcon from "../../../../assets/applications/OutlookIcon.svg";
import phoneIcon from "../../../../assets/icons/phone.svg";
import phoneWhiteIcon from "../../../../assets/icons/phoneWhite.svg";
import photosIcon from "../../../../assets/applications/WindowsPhotos.svg";
import powerpointIcon from "../../../../assets/applications/PowerpointIcon.svg";
import styles from "./EntriesTable.module.css";
import teamsIcon from "../../../../assets/applications/TeamsIcon.svg";
import { useDarkMode } from "@/shared/hooks/useDarkMode";
import type { RestrictedWord } from "@/shared/cache/slices/restrictedWords";
import { HighlightedNarrative } from "@/shared/components/HighlightedNarrative/HighlightedNarrative";
import userInputIcon from "../../../../assets/icons/userInput.svg";
import userInputWhiteIcon from "../../../../assets/icons/userInputWhite.svg";
import wordIcon from "../../../../assets/applications/WordIcon.svg";
import zoomIcon from "../../../../assets/applications/ZoomIcon.svg";

/** Format a time value (ISO string or "HH:MM:SS") to "H:MM AM/PM" */
export function formatTime(time?: string): string | null {
  if (!time) return null;
  let h: number, m: number;
  if (time.includes("T")) {
    const d = new Date(time);
    if (isNaN(d.getTime())) return null;
    h = d.getUTCHours();
    m = d.getUTCMinutes();
  } else {
    const [hStr, mStr] = time.split(":");
    h = parseInt(hStr, 10);
    m = parseInt(mStr, 10);
    if (isNaN(h) || isNaN(m)) return null;
  }
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hour12}:${String(m).padStart(2, "0")} ${period}`;
}

const APP_ICON_MAP: Record<string, string> = {
  "Microsoft Word": wordIcon,
  "Microsoft Outlook": outlookIcon,
  "Microsoft Outlook Calendar": calendarIcon,
  "Adobe Acrobat": adobeIcon,
  "Microsoft Powerpoint": powerpointIcon,
  "Microsoft Excel": excelIcon,
  "Zoom": zoomIcon,
  "Google Chrome": chromeIcon,
  "Microsoft Teams": teamsIcon,
  "Kofax": kofaxIcon,
  "Foxit": foxitIcon,
  "Photos": photosIcon,
};

export type EntryStatus = "tracked" | "exported" | "deleted";

export type Entry = {
  id: number;
  date: string;
  matter: string;
  matter_id?: string;
  matter_name?: string;
  client?: string;
  description: string;
  task: string;
  activity?: string;
  document_name?: string;
  duration: number;
  start_time?: string;
  end_time?: string;
  application?: string;
  /** Status for admin view badge display (tracked/exported/deleted) */
  status?: EntryStatus;
  /** Admin feedback on narrative - shows warning indicator when present */
  attorney_feedback?: string | null;
};

export type ColumnWidths = {
  date: number;
  matter: number;
  codes: number;
  narrative: number;
  units: number;
};

export type SortingMode = "time" | "matter";
export type SortDirection = "asc" | "desc";

export type Matter = {
  matter_id: string;
  task_group: string;
  activity_group: string;
};

export interface EntriesTableProps {
  darkMode?: boolean;
  entries: Entry[];
  // exportedEntries?: Entry[];
  groupedEntries: Record<string, Entry[]>;
  // groupedExportedEntries?: Record<string, Entry[]>;
  columnWidths: ColumnWidths;
  onResizeColumn: (key: keyof ColumnWidths, width: number) => void;
  onCascadingResize?: (resizingColumn: keyof ColumnWidths, deltaPercentage: number) => void;
  checkedRows: Set<number>;
  selectAllChecked: boolean;
  selectAllIndeterminate: boolean;
  onToggleSelectAll: () => void;
  onToggleRow: (id: number, e: React.MouseEvent) => void;
  onRowClick?: (entry: Entry) => void;
  onSortByMatter?: () => void;
  onSortByTime?: () => void;
  showCheckboxes?: boolean;
  mode?: "entries" | "history" | "deleted";
  matters?: Matter[];
  /** Padding inside resizable columns (px). Use for admin view to prevent content touching dividers. */
  cellPadding?: number;
  /** Current sorting mode - controls whether group totals are shown (only for "matter" mode) */
  sortingMode?: SortingMode;
  /** Current sort direction within the active sorting mode */
  sortDirection?: SortDirection;
  /** Map of entry IDs that failed export → error messages. Defaults to empty map. */
  failedExportEntries?: Map<number, string>;
  /** Set of entry IDs currently being exported (locked from editing). */
  exportingEntryIds?: Set<number>;
  /** Set of entry IDs with in-flight mutations (optimistic update visual feedback). */
  mutatingIds?: Set<number>;
  /** Custom message to show when entries are empty */
  emptyMessage?: string;
  /** Getter for restricted words scoped by client/matter */
  getRestrictedWords?: (client?: string, matter?: string) => RestrictedWord[];
  /** Auto-scroll to bottom on data change (e.g. only for today's date) */
  autoScrollToBottom?: boolean;
}

interface EntryRowProps {
  entry: Entry;
  darkMode: boolean;
  showCheckboxes: boolean;
  checked: boolean;
  isExporting: boolean;
  isMutating: boolean;
  onToggleRow: (id: number, e: React.MouseEvent) => void;
  onRowClick?: (entry: Entry) => void;
  columnWidths: ColumnWidths;
  onResizeColumn: (key: keyof ColumnWidths, width: number) => void;
  onCascadingResize?: (resizingColumn: keyof ColumnWidths, deltaPercentage: number) => void;
  cellPadding: number;
  incomplete: boolean;
  mode: "entries" | "history" | "deleted";
  failedExportEntries: Map<number, string>;
  getRestrictedWords?: (client?: string, matter?: string) => RestrictedWord[];
}

const EntryRow = memo(function EntryRow({
  entry,
  darkMode,
  showCheckboxes,
  checked,
  isExporting,
  isMutating,
  onToggleRow,
  onRowClick,
  columnWidths,
  onResizeColumn,
  onCascadingResize,
  cellPadding,
  incomplete,
  mode,
  failedExportEntries,
  getRestrictedWords,
}: EntryRowProps) {

  // Resolve restricted words for this entry's matter
  const restrictedWords = React.useMemo(() => {
    if (!getRestrictedWords) return undefined;
    if (!entry.matter_id) return getRestrictedWords();
    const [client, ...rest] = entry.matter_id.split('.');
    const matter = rest.join('.');
    return getRestrictedWords(client, matter);
  }, [getRestrictedWords, entry.matter_id]);

  const renderAppIcon = (application?: string) => {
    if (!application) return null;
    if (application === "Phone") return <img src={darkMode ? phoneWhiteIcon : phoneIcon} alt="Phone" className={styles.appLogo} />;
    if (application === "User Input") return <img src={darkMode ? userInputWhiteIcon : userInputIcon} alt="User" className={styles.appLogo} />;
    if (application === "Combine") return <img src={darkMode ? combineIconWhite : combineIcon} alt="Combine" className={styles.appLogo} />;
    if (application === "Duplicate") return <img src={darkMode ? duplicateIconWhite : duplicateIcon} alt="Duplicate" className={styles.appLogo} />;
    // Exact match first (preserves "Microsoft Outlook Calendar" vs "Microsoft Outlook"),
    // then case-insensitive substring fallback so backend process-name variants
    // (e.g. "ms-teams") still resolve. Mirrors ProcessingIndicator.tsx leniency.
    let icon: string | undefined = APP_ICON_MAP[application];
    if (!icon) {
      const appLower = application.toLowerCase();
      if (appLower.includes("calendar")) icon = calendarIcon;
      else if (appLower.includes("outlook")) icon = outlookIcon;
      else if (appLower.includes("word")) icon = wordIcon;
      else if (appLower.includes("adobe") || appLower.includes("acrobat")) icon = adobeIcon;
      else if (appLower.includes("powerpnt") || appLower.includes("powerpoint")) icon = powerpointIcon;
      else if (appLower.includes("excel")) icon = excelIcon;
      else if (appLower.includes("zoom")) icon = zoomIcon;
      else if (appLower.includes("chrome")) icon = chromeIcon;
      else if (appLower.includes("teams")) icon = teamsIcon;
      else if (appLower.includes("powerpdf") || appLower.includes("kofax")) icon = kofaxIcon;
      else if (appLower.includes("photos")) icon = photosIcon;
      else if (appLower.includes("foxit")) icon = foxitIcon;
    }
    return icon ? <img src={icon} alt={application} className={styles.appLogo} /> : null;
  };

  return (
    <div
      key={entry.id}
      className={`${styles.entryRow}${failedExportEntries.has(Number(entry.id)) ? ` ${styles.failedRow}` : ''}${isExporting ? ` ${styles.exportingRow}` : ''}${isMutating ? ` ${styles.mutatingRow}` : ''}`}
      onClick={(e) => {
        if (isExporting) return;
        const target = e.target as HTMLElement;
        if (target.closest(".item-checkbox") || target.closest("label")) return;
        if (showCheckboxes && (e.shiftKey || e.ctrlKey || e.metaKey)) {
          e.preventDefault();
          window.getSelection()?.removeAllRanges();
          onToggleRow(entry.id, e);
          return;
        }
        onRowClick?.(entry);
      }}
    >
      <div className={styles.entryLeft}>
        <div className={styles.entryCheckbox}>
          {showCheckboxes && (
            <label className={styles.checkboxHitArea} onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleRow(entry.id, e);
            }}>
              <input
                className="item-checkbox"
                type="checkbox"
                checked={checked}
                readOnly
              />
            </label>
          )}
        </div>
        <div className={styles.entryApp}>
          {renderAppIcon(entry.application)}
        </div>
        <ResizableColumn
          width={columnWidths.date}
          onResize={(w) => onResizeColumn("date", w)}
          onCascadingResize={(_, d) => onCascadingResize?.("date", d)}
          columnKey="date"
          className={styles.entryDate}
          contentPadding={cellPadding}
        >
          <div className={styles.dateTimeWrapper}>
            <span>{entry.date}</span>
            {entry.end_time && (
              <span className={styles.timeRange}>
                {formatTime(entry.end_time)}
              </span>
            )}
          </div>
        </ResizableColumn>
        <ResizableColumn
          width={columnWidths.matter}
          onResize={(w) => onResizeColumn("matter", w)}
          onCascadingResize={(_, d) => onCascadingResize?.("matter", d)}
          columnKey="matter"
          className={styles.matterWrapper}
          allowWrap
          contentPadding={cellPadding}
        >
          <div>
            {entry.matter_name}{entry.matter ? ` - ${entry.matter}` : ""}
          </div>
          {entry.client && (
            <div style={{ opacity: 0.7, fontSize: "0.9em" }}>
              {entry.client}
            </div>
          )}
        </ResizableColumn>
        <ResizableColumn
          width={columnWidths.codes}
          onResize={(w) => onResizeColumn("codes", w)}
          onCascadingResize={(_, d) => onCascadingResize?.("codes", d)}
          columnKey="codes"
          className={styles.codesWrapper}
          allowWrap
          contentPadding={cellPadding}
        >
          <div>{entry.task || ""}</div>
          {entry.activity && (
            <div>{entry.activity}</div>
          )}
        </ResizableColumn>
        <div className={styles.narrative} style={{ width: `${columnWidths.narrative}%` }}>
          {entry.attorney_feedback && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className={styles.feedbackWarning}>
                    <AlertTriangle className={styles.warningIcon} />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>This narrative has unresolved feedback</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <HighlightedNarrative text={entry.description} restrictedWords={restrictedWords} />
        </div>
      </div>
      <div className={styles.unitsWrapper}>
        {isExporting ? (
          <div className={styles.exportingBadge}>Exporting</div>
        ) : entry.status ? (
          <>
            {entry.status === "exported" && <div className={styles.exportedBadge}>Exported</div>}
            {entry.status === "deleted" && <div className={styles.incompleteBadge}>Deleted</div>}
            {entry.status === "tracked" && <div className={styles.trackedBadge}>Tracked</div>}
          </>
        ) : mode === "history" ? (
          <div className={styles.exportedBadge}>Exported</div>
        ) : mode === "deleted" ? (
          <div className={styles.incompleteBadge}>Deleted</div>
        ) : failedExportEntries.has(Number(entry.id)) ? (
          <div className={styles.failedBadge}>Failed</div>
        ) : (
          incomplete && (
            <div className={styles.incompleteBadge}>Incomplete</div>
          )
        )}
        <div className={styles.unitsBadge}>
          {(entry.duration * 100) % 10 !== 0 ? entry.duration.toFixed(2) : entry.duration.toFixed(1)}
        </div>
      </div>
    </div>
  );
});

const EntriesTable: React.FC<EntriesTableProps> = ({
  entries,
  groupedEntries,
  columnWidths,
  onResizeColumn,
  onCascadingResize,
  checkedRows,
  selectAllChecked,
  selectAllIndeterminate,
  onToggleSelectAll,
  onToggleRow,
  onRowClick,
  onSortByMatter,
  onSortByTime,
  showCheckboxes = true,
  mode = "entries",
  matters = [],
  cellPadding = 12,
  sortingMode = "time",
  sortDirection = "desc",
  failedExportEntries = new Map() as Map<number, string>,
  exportingEntryIds = new Set() as Set<number>,
  mutatingIds = new Set() as Set<number>,
  emptyMessage = "No transactions to see here...",
  getRestrictedWords,
  autoScrollToBottom = false,
}) => {
  const { darkMode } = useDarkMode();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const hasInitiallyScrolled = useRef(false);

  // Auto-scroll: bottom for today's entries, top for everything else.
  // For today (autoScrollToBottom=true), always scroll to bottom on new entries.
  // For other dates, only scroll on initial load — preserve position on data refreshes.
  // Date changes are handled by a `key` prop on <EntriesTable> which remounts the component.
  useEffect(() => {
    if (!scrollContainerRef.current || entries.length === 0) return;

    if (autoScrollToBottom) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
    } else if (!hasInitiallyScrolled.current) {
      scrollContainerRef.current.scrollTop = 0;
      hasInitiallyScrolled.current = true;
    }
  }, [autoScrollToBottom, groupedEntries, entries.length]);

  const isIncomplete = useCallback((e: Entry) => {
    const taskRequired = isTaskRequired(e.matter_id, matters);
    const activityRequired = isActivityRequired(e.matter_id, matters);
    return (
      !e.matter ||
      !e.description ||
      (taskRequired && !e.task) ||
      (activityRequired && !e.activity) ||
      e.description === "Please fill in the narrative."
    );
  }, [matters]);

  const selectedCount = checkedRows.size;

  const sortIcon = (column: SortingMode) => {
    if (sortingMode !== column) return <ArrowDownUp className="h-3 w-3" />;
    return sortDirection === "desc"
      ? <ArrowDown className="h-3 w-3" />
      : <ArrowUp className="h-3 w-3" />;
  };

  const sortTooltip = (column: SortingMode) => {
    if (sortingMode !== column) return column === "time" ? "Sort by time" : "Sort by matter";
    if (column === "time") return sortDirection === "desc" ? "Newest first" : "Oldest first";
    return sortDirection === "asc" ? "A to Z" : "Z to A";
  };

  const renderHeader = () => (
    <div className={styles.headerRow}>
      <div className={styles.headerLeft}>
        <div className={styles.headerCheckbox}>
          {showCheckboxes && (
            <label className={styles.checkboxHitArea}>
              <input
                type="checkbox"
                checked={selectAllChecked}
                ref={(el) => {
                  if (el) el.indeterminate = selectAllIndeterminate;
                }}
                onChange={onToggleSelectAll}
              />
            </label>
          )}
        </div>
        <div className={styles.headerApp}>
          {selectedCount > 0 && (
            <span className={styles.selectionCount}>{selectedCount}</span>
          )}
        </div>
        <ResizableColumn
          width={columnWidths.date}
          onResize={(w) => onResizeColumn("date", w)}
          onCascadingResize={(_, d) => onCascadingResize?.("date", d)}
          columnKey="date"
          className={styles.headerDate}
          onClick={onSortByTime}
          contentPadding={cellPadding}
        >
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex items-center gap-1">
                  Date
                  {sortIcon("time")}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>{sortTooltip("time")}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </ResizableColumn>
        <ResizableColumn
          width={columnWidths.matter}
          onResize={(w) => onResizeColumn("matter", w)}
          onCascadingResize={(_, d) => onCascadingResize?.("matter", d)}
          columnKey="matter"
          className={styles.headerMatter}
          onClick={onSortByMatter}
          contentPadding={cellPadding}
        >
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex items-center gap-1">
                  Matter
                  {sortIcon("matter")}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>{sortTooltip("matter")}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </ResizableColumn>
        <ResizableColumn
          width={columnWidths.codes}
          onResize={(w) => onResizeColumn("codes", w)}
          onCascadingResize={(_, d) => onCascadingResize?.("codes", d)}
          columnKey="codes"
          className={styles.headerCodes}
          contentPadding={cellPadding}
        >
          Codes
        </ResizableColumn>
        <div className={styles.headerNarrative} style={{ width: `${columnWidths.narrative}%` }}>
          Narrative
        </div>
      </div>
      <div className={styles.headerUnitsWrapper}>
        <div style={{ width: "100%", textAlign: "right" }}>Units</div>
      </div>
    </div>
  );

  const renderGroups = (groups: Record<string, Entry[]>) => {
    const keys = Object.keys(groups);
    return keys.map((k) => {
      const groupTotal = groups[k].reduce((sum, entry) => sum + (entry.duration || 0), 0);
      return (
        <div key={k}>
          <div className={styles.dateGroupHeader}>
            <div className={styles.dateText}>{k}</div>
            {sortingMode === "matter" && (
              <div className={styles.groupTotal}>{groupTotal.toFixed(1)} hrs</div>
            )}
          </div>
          {groups[k].map((entry) => (
            <EntryRow
              key={entry.id}
              entry={entry}
              darkMode={darkMode}
              showCheckboxes={showCheckboxes}
              checked={checkedRows.has(entry.id)}
              isExporting={exportingEntryIds.has(entry.id)}
              isMutating={mutatingIds.has(entry.id)}
              onToggleRow={onToggleRow}
              onRowClick={onRowClick}
              columnWidths={columnWidths}
              onResizeColumn={onResizeColumn}
              onCascadingResize={onCascadingResize}
              cellPadding={cellPadding}
              incomplete={isIncomplete(entry)}
              mode={mode}
              failedExportEntries={failedExportEntries}
              getRestrictedWords={getRestrictedWords}
            />
          ))}
        </div>
      );
    });
  };

  return (
    <>
      {renderHeader()}
      <div className={styles.scrollContainer} ref={scrollContainerRef}>
        {entries.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyText}>{emptyMessage}</div>
          </div>
        ) : (
          renderGroups(groupedEntries)
        )}
      </div>
    </>
  );
};

export default EntriesTable;
